import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Modal,
  SegmentedControl,
  PageHeader,
} from '@nexply/ui';
import { api } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { useTheme, type ThemePreference } from '../../lib/theme.js';
import { InviteLinkCard } from '../../components/InviteLinkCard.js';

export function SettingsPage() {
  const { user, refresh } = useAuth();
  const { preference, theme, setPreference } = useTheme();
  const nav = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [profileMsg, setProfileMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName ?? '');
      setLastName(user.lastName ?? '');
      setEmail(user.email);
    }
  }, [user]);

  const saveProfile = useMutation({
    mutationFn: () =>
      api.account.updateProfile({
        firstName: firstName.trim() ? firstName.trim() : null,
        lastName: lastName.trim() ? lastName.trim() : null,
        email,
      }),
    onSuccess: async () => {
      await refresh();
      setProfileMsg({ type: 'ok', text: 'Profil gespeichert.' });
    },
    onError: (e: { code?: string; message?: string }) => {
      setProfileMsg({
        type: 'err',
        text: e.code === 'email_taken' ? 'E-Mail-Adresse ist bereits vergeben.' : e.message ?? 'Speichern fehlgeschlagen.',
      });
    },
  });

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [pwMsg, setPwMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const changePw = useMutation({
    mutationFn: () => api.account.changePassword({ currentPassword: currentPw, newPassword: newPw }),
    onSuccess: () => {
      setCurrentPw('');
      setNewPw('');
      setPwMsg({ type: 'ok', text: 'Passwort geändert.' });
    },
    onError: (e: { code?: string }) => {
      setPwMsg({
        type: 'err',
        text: e.code === 'invalid_credentials' ? 'Aktuelles Passwort falsch.' : 'Änderung fehlgeschlagen.',
      });
    },
  });

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePw, setDeletePw] = useState('');
  const [deleteErr, setDeleteErr] = useState<string | null>(null);

  const deleteAccount = useMutation({
    mutationFn: () => api.account.deleteAccount({ currentPassword: deletePw }),
    onSuccess: async () => {
      await nav({ to: '/login' });
      window.location.reload();
    },
    onError: (e: { code?: string }) => {
      setDeleteErr(e.code === 'invalid_credentials' ? 'Passwort falsch.' : 'Löschen fehlgeschlagen.');
    },
  });

  function submitProfile(e: FormEvent) {
    e.preventDefault();
    setProfileMsg(null);
    saveProfile.mutate();
  }

  function submitPw(e: FormEvent) {
    e.preventDefault();
    if (newPw.length < 8) {
      setPwMsg({ type: 'err', text: 'Neues Passwort muss mindestens 8 Zeichen lang sein.' });
      return;
    }
    setPwMsg(null);
    changePw.mutate();
  }

  return (
    <div className="max-w-3xl space-y-6">
      <PageHeader
        title="Einstellungen"
        subtitle="Verwalte dein Konto, Passwort und Datenschutz."
      />

      <InviteLinkCard />

      <Card>
        <CardHeader>
          <CardTitle>Darstellung</CardTitle>
        </CardHeader>
        <CardBody className="pt-0 space-y-2">
          <SegmentedControl
            value={preference}
            onChange={(v) => setPreference(v as ThemePreference)}
            options={[
              { value: 'system', label: 'System' },
              { value: 'light', label: 'Hell' },
              { value: 'dark', label: 'Dunkel' },
            ]}
          />
          <p className="text-xs text-text-muted">
            {preference === 'system'
              ? `Folgt deinem Betriebssystem — aktuell ${theme === 'light' ? 'hell' : 'dunkel'}.`
              : 'Feste Auswahl, unabhängig vom Betriebssystem.'}
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Konto</CardTitle>
        </CardHeader>
        <CardBody className="pt-0">
          <form onSubmit={submitProfile} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Vorname</Label>
              <Input value={firstName} onChange={(e) => setFirstName(e.currentTarget.value)} autoComplete="given-name" />
            </div>
            <div>
              <Label>Nachname</Label>
              <Input value={lastName} onChange={(e) => setLastName(e.currentTarget.value)} autoComplete="family-name" />
            </div>
            <div>
              <Label>E-Mail</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} required />
            </div>
            <div className="md:col-span-2 flex items-center justify-end gap-3 pt-2">
              {profileMsg && (
                <span className={profileMsg.type === 'ok' ? 'text-xs text-success' : 'text-xs text-danger'}>
                  {profileMsg.text}
                </span>
              )}
              <Button type="submit" disabled={saveProfile.isPending}>
                {saveProfile.isPending ? 'Speichert…' : 'Speichern'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Passwort ändern</CardTitle>
        </CardHeader>
        <CardBody className="pt-0">
          <form onSubmit={submitPw} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Aktuelles Passwort</Label>
              <Input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.currentTarget.value)} required />
            </div>
            <div>
              <Label>Neues Passwort (min. 8 Zeichen)</Label>
              <Input type="password" value={newPw} onChange={(e) => setNewPw(e.currentTarget.value)} required minLength={8} />
            </div>
            <div className="md:col-span-2 flex items-center justify-end gap-3 pt-2">
              {pwMsg && (
                <span className={pwMsg.type === 'ok' ? 'text-xs text-success' : 'text-xs text-danger'}>
                  {pwMsg.text}
                </span>
              )}
              <Button type="submit" disabled={changePw.isPending}>
                {changePw.isPending ? 'Ändert…' : 'Passwort speichern'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <Card className="border-danger/25">
        <CardHeader>
          <CardTitle>Konto löschen</CardTitle>
        </CardHeader>
        <CardBody className="pt-0 space-y-3">
          <p className="text-sm text-text-dim">
            Beim Löschen werden alle deine Daten unwiderruflich entfernt – Mitglieder, Matches, Verbindungen und mehr.
          </p>
          <Button variant="danger" onClick={() => setDeleteOpen(true)}>
            Konto löschen
          </Button>
        </CardBody>
      </Card>

      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Konto wirklich löschen?" size="sm">
        <div className="space-y-4">
          <p className="text-sm text-text-dim">
            Diese Aktion kann nicht rückgängig gemacht werden. Bitte gib zur Bestätigung dein aktuelles Passwort ein.
          </p>
          <div>
            <Label>Aktuelles Passwort</Label>
            <Input
              type="password"
              value={deletePw}
              onChange={(e) => setDeletePw(e.currentTarget.value)}
              autoFocus
            />
          </div>
          {deleteErr && <div className="text-xs text-danger">{deleteErr}</div>}
          <div className="flex justify-end gap-2 pt-2 border-t border-line">
            <Button variant="ghost" onClick={() => setDeleteOpen(false)}>Abbrechen</Button>
            <Button
              variant="danger"
              disabled={!deletePw || deleteAccount.isPending}
              onClick={() => {
                setDeleteErr(null);
                deleteAccount.mutate();
              }}
            >
              {deleteAccount.isPending ? 'Lösche…' : 'Endgültig löschen'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
